import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import { MD2Colors, useTheme, Text } from 'react-native-paper';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { Outline } from './Outline';
const PickerListItem = ({ label, selected, onLayout }) => {
    const onPickerListItemLayout = (event) => {
        if (onLayout) {
            onLayout(event);
        }
    };
    const calculateSelectedLayout = useMemo(() => {
        return !selected
            ? {
                transform: [{ rotateX: '45deg' }, { scale: 0.8 }],
                opacity: 0.5,
            }
            : {};
    }, [selected]);
    return (_jsx(View, { style: [
            {
                justifyContent: 'center',
                padding: 5,
            },
            calculateSelectedLayout,
        ], onLayout: onPickerListItemLayout, children: _jsx(Text, { style: { textAlign: 'center' }, children: label }) }));
};
export const Picker = ({ label, items = [], onValueChange, value, animationDuration = 500, style, }) => {
    const VISIBLE_ITEMS = 3;
    const [itemHeight, setItemHeight] = useState(24);
    const position = useSharedValue(0);
    const theme = useTheme();
    const snapPoints = useMemo(() => {
        return items.map((_value, index) => -index * itemHeight);
    }, [itemHeight, items]);
    useEffect(() => {
        const indexOfSelection = items?.findIndex((item) => item.value === value);
        position.value = snapPoints[indexOfSelection];
    }, [itemHeight, value]);
    const styles = StyleSheet.create({
        requiredItemStyles: {
            backgroundColor: MD2Colors.transparent,
        },
        defaultSelectedItemStyle: {
            borderRadius: 4,
            backgroundColor: theme.colors.surfaceVariant,
        },
    });
    const calculateGestureHandlerRootViewStyle = useMemo(() => {
        return { height: itemHeight * VISIBLE_ITEMS };
    }, [itemHeight]);
    const calculateSelectedItemViewStyle = useMemo(() => {
        return { top: itemHeight, height: itemHeight };
    }, [itemHeight]);
    const getClosestSnapPoint = () => {
        return snapPoints.reduce((prev, curr, _idx) => {
            return Math.abs(curr - position.value) < Math.abs(prev - position.value) ? curr : prev;
        }, snapPoints[0]);
    };
    const onPickerListItemLayoutChange = (event) => {
        if (event.nativeEvent.layout.height > itemHeight) {
            setItemHeight(event.nativeEvent.layout.height);
        }
    };
    const gesture = Gesture.Pan()
        .runOnJS(true)
        .onChange((event) => {
        position.value = position.value + event.changeY;
    })
        .onEnd((_event) => {
        const maxPossible = -((items?.length - 1) * itemHeight);
        const pastTop = position.value > 0;
        const pastBottom = position.value < maxPossible;
        const closestSnapPoint = getClosestSnapPoint();
        position.value = pastTop ? 0 : pastBottom ? maxPossible : closestSnapPoint;
        if (onValueChange) {
            onValueChange(items[snapPoints.indexOf(closestSnapPoint)].value);
        }
    });
    const scrollAnimatedStyle = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: withTiming(position.value, {
                    easing: Easing.bezier(0.25, 1, 0.5, 1),
                    duration: animationDuration,
                }),
            },
        ],
    }), [position, animationDuration]);
    return (_jsx(Outline, { label: label, style: style, children: _jsx(GestureHandlerRootView, { style: calculateGestureHandlerRootViewStyle, children: _jsx(GestureDetector, { gesture: gesture, children: _jsxs(View, { style: [calculateGestureHandlerRootViewStyle, { overflow: 'hidden' }], children: [_jsx(View, { style: [styles.defaultSelectedItemStyle, calculateSelectedItemViewStyle] }), _jsx(Animated.View, { style: [scrollAnimatedStyle], children: items?.map((item, index) => (_jsx(PickerListItem, { label: item.label, selected: item.value === value, onLayout: onPickerListItemLayoutChange }, index))) })] }) }) }) }));
};
